import { useAnalytics, withAnalytics } from '@pco/analytics';
import { Box } from '@pco/atoms/Box';
import { Button, type ButtonProps } from '@pco/atoms/Button';
import { Icon } from '@pco/atoms/Icon';
import { getDataAttrs } from '@pco/utils';
import * as DialogPrimitive from '@radix-ui/react-dialog';
import { forwardRef, useEffect } from 'react';
import styled, { css, keyframes } from 'styled-components';
import { VisuallyHidden } from '../VisuallyHidden';
import DialogProvider, { type IDialogProps } from './DialogProvider';

const contentShow = keyframes`
  from {
    opacity: 0;
    transform: translate(-50%, -48%) scale(0.96);
  }
  to {
    opacity: 1;
    transform: translate(-50%, -50%) scale(1);
  }
`;

const dataAttrs = getDataAttrs(['atom', 'dialog']);

interface DialogWithProviderProps
	extends React.ComponentProps<typeof DialogPrimitive.Root>,
		IDialogProps {}

const DialogMain = ({ ...props }: DialogWithProviderProps) => {
	const fireAnalytics = useAnalytics('Dialog');
	const { open, onOpenChange, ...rest } = props;

	// biome-ignore lint/correctness/useExhaustiveDependencies: unneeded fireAnalytics dependency
	useEffect(() => {
		// For controlled mode: when using 'open' prop
		if (open) {
			fireAnalytics('view');
			onOpenChange?.(open);
		}
	}, [open]);

	const handleOpenChange = (isOpen: boolean) => {
		// For non controlled mode: when the dialog state is changed with the component Trigger.
		if (isOpen) {
			fireAnalytics('view');
		}
		onOpenChange?.(isOpen);
	};

	return (
		<DialogPrimitive.Root
			open={open}
			onOpenChange={handleOpenChange}
			{...rest}
		/>
	);
};

export const DialogWithProvider = (props: DialogWithProviderProps) => {
	return (
		<DialogProvider {...props}>
			<DialogMain {...props} />
		</DialogProvider>
	);
};

const DialogTrigger = styled(DialogPrimitive.Trigger).attrs(
	dataAttrs('trigger'),
)``;
const DialogPortal = styled(DialogPrimitive.Portal)``;

const DialogOverlay = styled(DialogPrimitive.Overlay).attrs(
	dataAttrs('overlay'),
)`
${({ theme }) => {
	const { semanticTokens } = theme;
	return css`
	position: fixed;
	inset: 0;
	background-color: ${({ theme }) => theme.semanticTokens.colors.background.skrim};
	z-index: ${semanticTokens.layers.overlay};
`;
}}`;

const DialogTitle = styled(DialogPrimitive.Title).attrs(dataAttrs('title'))``;
const DialogDescription = styled(DialogPrimitive.Description).attrs(
	dataAttrs('description'),
)``;

const DialogClose = styled(DialogPrimitive.Close).attrs(dataAttrs('close'))`
`;

const DialogSave = styled(DialogPrimitive.Close).attrs(dataAttrs('save'))``;

const StyledDialogContent = styled(DialogPrimitive.Content).attrs(
	dataAttrs('overlay-content'),
)<{
	$width?: string | number;
}>`
	${({ theme, $width }) => {
		const { semanticTokens, breakpoints } = theme;
		// `css` helper to wrap the keyframe - not supported on v3
		return css`
		z-index: ${semanticTokens.layers.overlay + 1};
		position: fixed;
		background: ${semanticTokens.colors.background.primary};
		border-radius: ${semanticTokens.radius.tooltips};
		top: 50%;
		left: 50%;
		transform: translate(-50%, -50%);
		animation: ${contentShow} 150ms cubic-bezier(0.16, 1, 0.3, 1);

		width: ${$width || '100%'};
		max-width: 90vw;

		height: auto;

		max-height: 90vh;
		overflow-y: auto;

		&::-webkit-scrollbar {
			display: none;
		}
		-ms-overflow-style: none; /* Internet Explorer 10+ */
		scrollbar-width: none; /* Firefox */

		& [data-part="header"] {
			padding-block: 32px 20px;
			padding-inline: 20px;
		}

		& [data-part="body"] {
			padding-inline: 20px;
		}

		& [data-part="footer"] {
			padding-block: 32px;
			padding-inline: 20px;
		}

		${breakpoints('tablet', 'up')} {
			max-width: 640px;

			& [data-part="header"],
			& [data-part="body"],
			& [data-part="footer"] {
				padding-inline: 32px;
			}
		}
		${breakpoints('desktop', 'up')} {
			max-width: 700px;
		}
    `;
	}}
`;

const DialogContent = forwardRef<
	React.ElementRef<typeof DialogPrimitive.Content>,
	React.ComponentPropsWithoutRef<typeof DialogPrimitive.Content> & {
		width?: number | string;
		height?: number;
		closeBtn?: React.ReactNode;
	}
>(({ children, closeBtn, width, ...props }, ref) => {
	return (
		<DialogPortal>
			<DialogOverlay>
				{closeBtn && <Dialog.Close asChild>{closeBtn}</Dialog.Close>}
			</DialogOverlay>
			<StyledDialogContent ref={ref} $width={width} {...props}>
				<VisuallyHidden>
					<Dialog.Header />
				</VisuallyHidden>
				{children}
			</StyledDialogContent>
		</DialogPortal>
	);
});

const DialogBody = styled.div.attrs(dataAttrs('body'))`
			display: flex;
			padding-block: 20px;
			flex-direction: column;
			align-items: flex-start;
			gap: 10px;
`;

const CloseButton = ({
	children = <Icon fallbackIcon="close" />,
	...rest
}: ButtonProps) => (
	<DialogPrimitive.Close asChild>
		<StyledCloseButton aria-label="Close overlay" {...rest}>
			{children}
		</StyledCloseButton>
	</DialogPrimitive.Close>
);

const HeaderStyled = styled.div.attrs(dataAttrs('header'))`
	display: flex;
	align-items: center;
	h2 {
		max-width: 80%;
	}
`;

const HeaderContent = styled(Box).attrs({
	display: 'flex',
	flexDirection: 'column',
	alignItems: 'flex-start',
	gap: '12px',
	flex: '1 0 0',
})``;

const StyledCloseButton = styled(Button).attrs({
	_size: 'iconMd',
})`

	${({ theme }) => {
		const { breakpoints } = theme;
		return `
			position: absolute;
			right: 20px;
			top: 32px;
			width: 48px;
			height: 32px;
			${breakpoints('tablet', 'up')} {
				right: 32px;
			}
		`;
	}}
`;

const DialogHeader: React.FC<{
	children?: React.ReactNode;
	closeButton?: React.ReactNode;
	style?: React.CSSProperties;
}> = ({ children, closeButton = <CloseButton />, style }) => (
	<HeaderStyled style={style}>
		{closeButton}
		<HeaderContent>{children}</HeaderContent>
	</HeaderStyled>
);

const DialogFooter = styled.footer.attrs(dataAttrs('footer'))`
	${({ theme }) => {
		const { semanticTokens } = theme;
		return `
			display: flex;
			align-items: center;
			width: 100%;
			border-top: 1px solid ${semanticTokens.colors.border.lineAlpha};
		`;
	}}
`;

export const Dialog = Object.assign(
	withAnalytics(DialogWithProvider)({ _component: 'Dialog' }),
	{
		Portal: DialogPortal,
		Overlay: DialogOverlay,
		Trigger: DialogTrigger,
		Close: DialogClose,
		Save: DialogSave,
		Content: DialogContent,
		Header: DialogHeader,
		Body: DialogBody,
		Footer: DialogFooter,
		Title: DialogTitle,
		Description: DialogDescription,
		CloseButton,
	},
);
